import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  HiOutlineRefresh,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineStar,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineMenuAlt4,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineChevronDown,
  HiOutlineChevronUp
} from 'react-icons/hi'
import projectMindmapService from '../../services/projectMindmapService'

/**
 * ProjectMindmapAdmin - Admin page for managing project mindmaps
 * Features: Sync from DXMT, CRUD, Drag-drop reorder
 */
export default function ProjectMindmapAdmin() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState(null)
  const [editingProject, setEditingProject] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [draggedItem, setDraggedItem] = useState(null)

  useEffect(() => {
    loadProjects()
    loadSyncStatus()
  }, [])

  const loadProjects = async () => {
    setLoading(true)
    const result = await projectMindmapService.getAdminProjects()
    if (result.status === 'success') {
      setProjects(result.data || [])
    }
    setLoading(false)
  }

  const loadSyncStatus = async () => {
    const result = await projectMindmapService.getSyncStatus()
    if (result.status === 'success') {
      setSyncStatus(result.data)
    }
  }

  const handleSync = async () => {
    if (syncing) return

    setSyncing(true)
    const toastId = toast.loading('Đang đồng bộ từ DXMT...')

    const result = await projectMindmapService.syncFromDXMT()

    if (result.status === 'success') {
      toast.success(
        `Đồng bộ thành công! Thêm: ${result.data?.added || 0}, Cập nhật: ${result.data?.updated || 0}`,
        { id: toastId }
      )
      loadProjects()
      loadSyncStatus()
    } else {
      toast.error(result.message || 'Đồng bộ thất bại', { id: toastId })
    }

    setSyncing(false)
  }

  const handleToggleFeatured = async (project) => {
    const result = await projectMindmapService.toggleFeatured(project.id)
    if (result.status === 'success') {
      setProjects(prev =>
        prev.map(p =>
          p.id === project.id ? { ...p, is_featured: result.data.is_featured } : p
        )
      )
      toast.success(result.data.is_featured ? 'Đã đánh dấu nổi bật' : 'Đã bỏ đánh dấu nổi bật')
    }
  }

  const handleToggleStatus = async (project) => {
    const result = await projectMindmapService.toggleStatus(project.id)
    if (result.status === 'success') {
      setProjects(prev =>
        prev.map(p =>
          p.id === project.id ? { ...p, status: result.data.status } : p
        )
      )
      toast.success(result.data.status === 'active' ? 'Đã hiển thị' : 'Đã ẩn')
    }
  }

  const handleDelete = async (project) => {
    if (!confirm(`Xóa dự án "${project.name}"?`)) return

    const result = await projectMindmapService.deleteProject(project.id)
    if (result.status === 'success') {
      setProjects(prev => prev.filter(p => p.id !== project.id))
      toast.success('Đã xóa dự án')
    } else {
      toast.error(result.message)
    }
  }

  const handleEdit = async (project) => {
    const result = await projectMindmapService.getAdminProject(project.id)
    if (result.status === 'success') {
      setEditingProject(result.data)
      setShowEditModal(true)
    }
  }

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    if (draggedItem === null || draggedItem === index) return

    const newProjects = [...projects]
    const draggedProject = newProjects[draggedItem]
    newProjects.splice(draggedItem, 1)
    newProjects.splice(index, 0, draggedProject)

    setProjects(newProjects)
    setDraggedItem(index)
  }

  const handleDragEnd = async () => {
    if (draggedItem === null) return

    // Save new order
    const items = projects.map((p, index) => ({
      id: p.id,
      display_order: index
    }))

    const result = await projectMindmapService.reorderProjects(items)
    if (result.status !== 'success') {
      toast.error('Lưu thứ tự thất bại')
      loadProjects()
    }

    setDraggedItem(null)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">
              Quản lý Mindmap dự án
            </h1>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSync}
                disabled={syncing}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
                  ${syncing
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                  }
                `}
              >
                <HiOutlineRefresh className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                Sync từ DXMT
              </button>
            </div>
          </div>

          {/* Sync Status */}
          {syncStatus?.current && (
            <div className="mt-3 text-sm text-gray-500">
              Lần sync cuối:{' '}
              {syncStatus.current.last_sync
                ? new Date(syncStatus.current.last_sync).toLocaleString('vi-VN')
                : 'Chưa sync'
              }
              {syncStatus.current.stats && (
                <span className="ml-2">
                  (Thêm: {syncStatus.current.stats.added},
                  Cập nhật: {syncStatus.current.stats.updated})
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border">
            <p className="text-gray-500 mb-4">Chưa có dự án nào</p>
            <button
              onClick={handleSync}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <HiOutlineRefresh className="w-5 h-5" />
              Sync từ DXMT API
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b text-sm font-medium text-gray-500">
              <div className="col-span-1"></div>
              <div className="col-span-5">Tên dự án</div>
              <div className="col-span-2 text-center">Nhánh</div>
              <div className="col-span-2 text-center">Trạng thái</div>
              <div className="col-span-2 text-right">Thao tác</div>
            </div>

            {/* Table Body */}
            <div className="divide-y">
              {projects.map((project, index) => (
                <div
                  key={project.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`
                    grid grid-cols-12 gap-4 px-4 py-3 items-center
                    hover:bg-gray-50 cursor-grab active:cursor-grabbing
                    ${draggedItem === index ? 'bg-primary-50' : ''}
                    ${project.status === 'hidden' ? 'opacity-60' : ''}
                  `}
                >
                  {/* Drag Handle */}
                  <div className="col-span-1">
                    <HiOutlineMenuAlt4 className="w-5 h-5 text-gray-400" />
                  </div>

                  {/* Name */}
                  <div className="col-span-5 flex items-center gap-3">
                    {project.avatar_url && (
                      <img
                        src={project.avatar_url}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    )}
                    <div>
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {project.name}
                        {project.is_featured && (
                          <HiOutlineStar className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                      {project.dxmt_id && (
                        <div className="text-xs text-gray-400">
                          DXMT: {project.dxmt_id.slice(0, 8)}...
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Branch Count */}
                  <div className="col-span-2 text-center">
                    <span className="px-2 py-1 bg-gray-100 rounded text-sm text-gray-600">
                      {project.branch_count || 0}/6
                    </span>
                  </div>

                  {/* Status */}
                  <div className="col-span-2 text-center">
                    <button
                      onClick={() => handleToggleStatus(project)}
                      className={`
                        inline-flex items-center gap-1 px-2 py-1 rounded text-sm
                        ${project.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                        }
                      `}
                    >
                      {project.status === 'active' ? (
                        <>
                          <HiOutlineEye className="w-4 h-4" />
                          Hiển thị
                        </>
                      ) : (
                        <>
                          <HiOutlineEyeOff className="w-4 h-4" />
                          Ẩn
                        </>
                      )}
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <button
                      onClick={() => handleToggleFeatured(project)}
                      className={`p-2 rounded-lg transition-colors ${
                        project.is_featured
                          ? 'text-amber-500 bg-amber-50'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                      title={project.is_featured ? 'Bỏ nổi bật' : 'Đánh dấu nổi bật'}
                    >
                      <HiOutlineStar className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleEdit(project)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Chỉnh sửa"
                    >
                      <HiOutlinePencil className="w-5 h-5" />
                    </button>
                    <Link
                      to={`/projects/${project.slug}`}
                      target="_blank"
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Xem"
                    >
                      <HiOutlineEye className="w-5 h-5" />
                    </Link>
                    <button
                      onClick={() => handleDelete(project)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Xóa"
                    >
                      <HiOutlineTrash className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingProject && (
        <EditProjectModal
          project={editingProject}
          onClose={() => {
            setShowEditModal(false)
            setEditingProject(null)
          }}
          onSave={() => {
            setShowEditModal(false)
            setEditingProject(null)
            loadProjects()
          }}
        />
      )}
    </div>
  )
}

/**
 * EditProjectModal - Modal for editing project branches
 */
function EditProjectModal({ project, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: project.name || '',
    overview: project.branches?.overview?.items || [],
    location: project.branches?.location?.items || [],
    design: project.branches?.design?.items || [],
    amenities: project.branches?.amenities?.items || [],
    pricing: project.branches?.pricing?.items || [],
    progress: project.branches?.progress?.items || []
  })
  const [saving, setSaving] = useState(false)
  const [expandedBranch, setExpandedBranch] = useState('pricing') // Default expand pricing (admin adds this)

  const branchLabels = {
    overview: 'Tổng quan',
    location: 'Vị trí',
    design: 'Thiết kế',
    amenities: 'Tiện ích',
    pricing: 'Giá & CSBH',
    progress: 'Tiến độ'
  }

  const handleSave = async () => {
    setSaving(true)

    const result = await projectMindmapService.updateProject(project.id, {
      name: formData.name,
      overview: formData.overview,
      location: formData.location,
      design: formData.design,
      amenities: formData.amenities,
      pricing: formData.pricing,
      progress: formData.progress
    })

    if (result.status === 'success') {
      toast.success('Đã lưu thay đổi')
      onSave()
    } else {
      toast.error(result.message)
    }

    setSaving(false)
  }

  const updateBranchItem = (branch, index, value) => {
    setFormData(prev => ({
      ...prev,
      [branch]: prev[branch].map((item, i) => i === index ? value : item)
    }))
  }

  const addBranchItem = (branch) => {
    setFormData(prev => ({
      ...prev,
      [branch]: [...prev[branch], '']
    }))
  }

  const removeBranchItem = (branch, index) => {
    setFormData(prev => ({
      ...prev,
      [branch]: prev[branch].filter((_, i) => i !== index)
    }))
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            Chỉnh sửa: {project.name}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tên dự án
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Branches */}
          {Object.keys(branchLabels).map(branch => (
            <div key={branch} className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedBranch(expandedBranch === branch ? null : branch)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
              >
                <span className="font-medium text-gray-900">
                  {branchLabels[branch]}
                  <span className="ml-2 text-sm text-gray-500">
                    ({formData[branch].length} mục)
                  </span>
                </span>
                {expandedBranch === branch ? (
                  <HiOutlineChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <HiOutlineChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {expandedBranch === branch && (
                <div className="p-4 space-y-2">
                  {formData[branch].map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateBranchItem(branch, index, e.target.value)}
                        placeholder={`Mục ${index + 1}...`}
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeBranchItem(branch, index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addBranchItem(branch)}
                    className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <HiOutlinePlus className="w-4 h-4" />
                    Thêm mục
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Hủy
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Đang lưu...
              </>
            ) : (
              <>
                <HiOutlineCheck className="w-5 h-5" />
                Lưu thay đổi
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
